/**
 * PhoneCard — ONE card shape for everything in the studio's phone strip.
 *
 * The live preview, the focused module's top-down view and every connection
 * card are the same object: the same frame, the same box, the same scale, the
 * same caption block underneath. That uniformity is the point — the strip has
 * no differently-shaped zones left to fall out of alignment, so "make them
 * line up" stops being a layout problem.
 *
 * The scale is FIXED (`phoneMetrics`), not fitted to the box. `PhonePreview`
 * measures its container and rescales asynchronously, which is fine on its own
 * but means every rect inside it moves one frame after mount — and the focus
 * wire, which anchors to module boxes, would then be measured against a layout
 * that no longer exists. A constant transform makes the geometry true from the
 * first paint.
 *
 * Children render in PHONE coordinates (390×844) inside a positioned wrapper,
 * so host chrome — the '+' capture button, the back chevron, the capture sheet — can be
 * absolutely positioned in phone pixels and scales with everything else.
 *
 * `frameRef` exposes the screen box, which is what the wire anchors against.
 *
 * `framed` draws the card's outer boundary. The live preview keeps it — it is
 * a phone you are using. The focus and connection cards do NOT: they are
 * diagram nodes the wire runs into, and a boundary there only gives the eye
 * (and `overflow: hidden`) something to cut the wire against.
 *
 * `zone` names the ZONE this card opens — one title per zone, never per card,
 * so the connections run carries it on the card that leads it. See `ZoneLabel`.
 */
import { useEffect, useRef } from 'react';
import { PHONE_W, PHONE_H, PHONE_SCALE, SCREEN_W, SCREEN_H, ZONE_LABEL_H } from './phoneMetrics';

const C = { text: '#1c1c1e', dim: '#6e6e73' };

/**
 * The scrollable element under the pointer, INSIDE this phone — the app's own
 * page scroller (a PageShell body). `elementsFromPoint` still reaches
 * it through the focus card's inspect overlay, which sits on top but is not
 * itself scrollable.
 */
function innerScrollerAt(x, y, root) {
  for (const el of (globalThis.document?.elementsFromPoint?.(x, y) || [])) {
    if (el === root || !root.contains(el)) continue;
    if (el.scrollHeight - el.clientHeight > 1) {
      const oy = getComputedStyle(el).overflowY;
      if (oy === 'auto' || oy === 'scroll') return el;
    }
  }
  return null;
}

export const CARD_W = SCREEN_W;
export const CARD_H = SCREEN_H;

/**
 * THE BEZEL IS NOT A SCROLLER — and `overflow: hidden` does not say that.
 *
 * `hidden` only hides the scrollbar: the box is still a scroll CONTAINER, and
 * anything that scrolls it — a browser scrolling a focused control into view
 * after a real click, `scrollIntoView`, assistive tech — moves the whole phone
 * inside its frame. That is not theoretical. The live preview keeps the capture
 * sheet mounted at all times (it slides in and out), and a CLOSED sheet is
 * parked exactly one phone-height below the page by `translateY(100%)`. A
 * transformed descendant still counts toward scrollable overflow, so the frame's
 * scroll height was exactly TWICE its height, and one real click on a control
 * near the bottom of a short page (scene-studio's `Try`, in the pinned action
 * bar) let the browser scroll the bezel to its end — which slid the closed sheet
 * perfectly over the page. It read as "the widget opened a capture sheet"; it
 * was a closed sheet, `aria-hidden` and all, scrolled into view.
 *
 * `overflow: clip` is the declaration that means what this frame means: clip the
 * paint, and be NO scroll container at all (`scrollTop` cannot even be set).
 * `hidden` stays as the first declaration so an engine without `clip` keeps
 * today's behaviour instead of falling back to `visible`, and the rule lives in
 * a stylesheet because that is the only way to write both.
 *
 * It must NOT be an inline style: inline wins over the class, and the whole
 * point is the second declaration.
 */
const FRAME_CSS = `
.vi-phone-frame { overflow: hidden; overflow: clip; }
`;

function FrameStyles() {
  return <style>{FRAME_CSS}</style>;
}

/**
 * A zone title, in the band every strip item reserves (`ZONE_LABEL_H`).
 *
 * It is ABSOLUTELY positioned into that band rather than flowed above the card,
 * for two reasons that both matter: the frame must stay the card's first flow
 * child — the studio measures it to anchor the wires — and a title must be free
 * to run past its own card, because a zone is a RUN of cards (the connections)
 * and gets exactly one title, at the run's left edge.
 */
export function ZoneLabel({ children }) {
  if (!children) return null;
  return (
    <span style={{
      position: 'absolute', left: 0, top: 0, height: ZONE_LABEL_H, lineHeight: `${ZONE_LABEL_H}px`,
      whiteSpace: 'nowrap', pointerEvents: 'none',
      fontSize: 10, fontWeight: 800, letterSpacing: '.09em', textTransform: 'uppercase', color: C.dim,
    }}>{children}</span>
  );
}

export default function PhoneCard({
  children, title, sub, active = false, accent = null, onClick = null,
  framed = true, frameRef = null, testId = null, frameTestId = null, zone = null,
}) {
  // The card owns the wheel so the phone behaves like a phone: a vertical wheel
  // scrolls the app's OWN scroller and stops there — it never chains out to the
  // studio page or the horizontal strip. React's `onWheel` is passive (its
  // `preventDefault` is a no-op), so this is a native non-passive listener; it
  // sits on the card (an ancestor of the frame and of the focus card's inspect
  // overlay), so it still fires for a wheel that lands on that overlay. The
  // phone is scaled, so a screen delta covers `1/scale` phone pixels. Horizontal
  // gestures fall through untouched, so the strip and in-app rails scroll sideways.
  const cardRef = useRef(null);
  useEffect(() => {
    const card = cardRef.current;
    if (!card) return undefined;
    const onWheel = (e) => {
      if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) return; // horizontal → native
      const sc = innerScrollerAt(e.clientX, e.clientY, card);
      if (sc) sc.scrollTop += e.deltaY / PHONE_SCALE;
      e.preventDefault();
    };
    card.addEventListener('wheel', onWheel, { passive: false });
    return () => card.removeEventListener('wheel', onWheel);
  }, []);
  return (
    <div
      ref={cardRef}
      data-card="1"
      data-testid={testId || undefined}
      data-active={active ? '1' : undefined}
      onClick={onClick || undefined}
      style={{
        flex: '0 0 auto', width: CARD_W, display: 'flex', flexDirection: 'column', gap: 8,
        // the zone band, reserved whether or not THIS card carries the title
        position: 'relative', paddingTop: ZONE_LABEL_H,
        cursor: onClick && !active ? 'pointer' : 'default',
        opacity: onClick && !active ? 0.72 : 1,
      }}
    >
      <div
        ref={frameRef}
        data-testid={frameTestId || undefined}
        // ALWAYS clip the card's own page: the page is 844px tall and its
        // internal scroller only works if the card bounds it. This never
        // touched the wire — the wire's SVG is a SIBLING of the cards in the
        // strip, not a descendant, so a card cannot clip it. The rule is a
        // CLASS, not an inline style, and never both: see FRAME_CSS.
        className="vi-phone-frame"
        style={{
          width: CARD_W, height: CARD_H, position: 'relative',
          borderRadius: framed ? 18 : 0,
          background: framed ? '#fff' : 'transparent',
          border: framed ? `1px solid ${active && accent ? accent : 'rgba(0,0,0,.14)'}` : 'none',
          outline: framed && active && accent ? `2px solid ${accent}` : 'none', outlineOffset: 2,
          boxShadow: framed ? '0 2px 10px rgba(0,0,0,.06)' : 'none',
        }}
      >
        <div style={{
          width: PHONE_W, height: PHONE_H, position: 'relative',
          transform: `scale(${PHONE_SCALE})`, transformOrigin: 'top left',
        }}>
          {children}
        </div>
      </div>
      <div style={{ width: CARD_W, display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0 }}>
        <code style={{
          fontSize: 11.5, fontWeight: 700, color: active && accent ? accent : C.text,
          // A COLUMN of rows, never a clamp: a card id carries a uuid
          // (`shelves__card__027f397b-…`) that filled both lines of the old
          // 2-line clamp and pushed the component TYPE out of the box. Each row
          // decides its own overflow — the id may ellipsis, the type never does.
          display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0, lineHeight: 1.3,
        }}>{title}</code>
        <span style={{ fontSize: 10.5, color: C.dim, lineHeight: 1.4, wordBreak: 'break-word' }}>{sub}</span>
      </div>
      {/* LAST in the DOM, FIRST on screen — see ZoneLabel: the frame has to
          stay `children[0]`, which is what the studio measures. The frame's own
          stylesheet rides here for the same reason: anything before the frame
          takes its place as the card's first child. */}
      <ZoneLabel>{zone}</ZoneLabel>
      <FrameStyles />
    </div>
  );
}
